import { useState } from "react";
import { useTabDraft } from "@/components/ui/box-draft";
import type { Result } from "@/components/ui/box.schema";
import { recover, send } from "./admissions-state";

/** Each job keeps its command key with its tab draft, including across reload. */
export function useApplicantCommand<V, T>(box: string, record: string, version: string | null, initial: V, settle: (answer: T, key?: string) => Promise<Result> | Result, onConflict?: (error: unknown) => void) {
  const keep = useTabDraft<V, string>({ box, record, version, initial });
  const [results, setResults] = useState<Record<string, Result>>({});
  const remember = (value: Result): Result => {
    if (value.state === "refused") keep.setPending(null);
    if (value.state === "confirmed") keep.setPending(null);
    const held: Result = value.state === "unknown" ? { ...value, check: { ...value.check, run: async () => remember(await value.check.run()) } } : value;
    setResults(current => ({ ...current, [record]: held })); return held;
  };
  const stored = results[record] ?? { state: "none" as const };
  const bind = (value: Result): Result => value.state === "unknown" ? { ...value, check: { ...value.check, run: async () => remember(await value.check.run()) } } : value;
  const result = stored.state === "none" && keep.pending ? bind(recover<T>(keep.pending, answer => settle(answer, keep.pending!))) : stored;
  return { keep, result, accept: remember,
    reset: () => setResults(current => ({ ...current, [record]: { state: "none" } })),
    run: async (key: string, command: () => Promise<T>) => {
      keep.setPending(key);
      return remember(await send(key, command, answer => settle(answer, key), onConflict));
    } };
}
